import React from 'react';

// ─── Types ───────────────────────────────────────────────

interface InnerWeatherIconProps {
  a: number;        // neuroticism (0-100): low = calm, high = anxious
  b: number;        // well-being (0-100): low = struggling, high = thriving
  c: number;        // self-efficacy (0-100): low = low confidence, high = capable
  className?: string;
  showLegend?: boolean; // default true
}

type WeatherState =
  | 'sunny'
  | 'mostly-sunny'
  | 'partly-cloudy'
  | 'cloudy'
  | 'drizzle'
  | 'rainy'
  | 'stormy';

// ─── Weather state logic ─────────────────────────────────

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

function getWeatherState(a: number, b: number, c: number): WeatherState {
  const adjustedA = 100 - a; // invert neuroticism so higher = better
  const composite = (adjustedA + b + c) / 3;

  if (composite >= 85) return 'sunny';
  if (composite >= 70) return 'mostly-sunny';
  if (composite >= 55) return 'partly-cloudy';
  if (composite >= 40) return 'cloudy';
  if (composite >= 25) return 'drizzle';
  if (composite >= 10) return 'rainy';
  return 'stormy';
}

// ─── SVG Weather Icons ───────────────────────────────────
// All drawn in a ~60x60 area centered at (100,100) in a 200x200 viewBox.
// Stroke-only, no fill, matching Heroicons outline style.

const S = 3;
const LC = 'round';
const LJ = 'round';

const cloudPath = (cx: number, cy: number) =>
  `M${cx - 24},${cy + 6} h48 a6,6 0 0 0 0,-12 h-4 a16,16 0 0 0 -31,-6 a12,12 0 0 0 -19,12 a6,6 0 0 0 6,6 Z`;

const smallCloudPath = (cx: number, cy: number) =>
  `M${cx - 16},${cy + 4} h32 a4,4 0 0 0 0,-8 h-2 a11,11 0 0 0 -21,-4 a8,8 0 0 0 -13,8 a4,4 0 0 0 4,4 Z`;

const SunnyIcon = () => (
  <g>
    <circle cx={100} cy={100} r={14} strokeWidth={S} stroke="currentColor" fill="none" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
      const rad = (angle * Math.PI) / 180;
      return (
        <line
          key={angle}
          x1={100 + Math.cos(rad) * 20} y1={100 + Math.sin(rad) * 20}
          x2={100 + Math.cos(rad) * 27} y2={100 + Math.sin(rad) * 27}
          strokeWidth={S} stroke="currentColor" strokeLinecap={LC}
        />
      );
    })}
  </g>
);

const MostlySunnyIcon = () => (
  <g>
    <circle cx={90} cy={90} r={12} strokeWidth={S} stroke="currentColor" fill="none" />
    {[180, 225, 270, 315, 0].map((angle) => {
      const rad = (angle * Math.PI) / 180;
      return (
        <line
          key={angle}
          x1={90 + Math.cos(rad) * 17} y1={90 + Math.sin(rad) * 17}
          x2={90 + Math.cos(rad) * 23} y2={90 + Math.sin(rad) * 23}
          strokeWidth={S} stroke="currentColor" strokeLinecap={LC}
        />
      );
    })}
    <path d={smallCloudPath(108, 110)} strokeWidth={S} stroke="currentColor" fill="none" strokeLinejoin={LJ} />
  </g>
);

const PartlyCloudyIcon = () => (
  <g>
    <circle cx={88} cy={88} r={11} strokeWidth={S} stroke="currentColor" fill="none" />
    {[180, 225, 270, 315].map((angle) => {
      const rad = (angle * Math.PI) / 180;
      return (
        <line
          key={angle}
          x1={88 + Math.cos(rad) * 16} y1={88 + Math.sin(rad) * 16}
          x2={88 + Math.cos(rad) * 22} y2={88 + Math.sin(rad) * 22}
          strokeWidth={S} stroke="currentColor" strokeLinecap={LC}
        />
      );
    })}
    <path d={cloudPath(104, 106)} strokeWidth={S} stroke="currentColor" fill="none" strokeLinejoin={LJ} />
  </g>
);

const CloudyIcon = () => (
  <g>
    <path d={cloudPath(100, 100)} strokeWidth={S} stroke="currentColor" fill="none" strokeLinejoin={LJ} />
  </g>
);

const DrizzleIcon = () => (
  <g>
    <path d={cloudPath(100, 94)} strokeWidth={S} stroke="currentColor" fill="none" strokeLinejoin={LJ} />
    <line x1={88} y1={108} x2={86} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
    <line x1={100} y1={108} x2={98} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
    <line x1={112} y1={108} x2={110} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
  </g>
);

const RainyIcon = () => (
  <g>
    <path d={cloudPath(100, 90)} strokeWidth={S} stroke="currentColor" fill="none" strokeLinejoin={LJ} />
    <line x1={85} y1={104} x2={82} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
    <line x1={95} y1={104} x2={92} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
    <line x1={105} y1={104} x2={102} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
    <line x1={115} y1={104} x2={112} y2={114} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
  </g>
);

const StormyIcon = () => (
  <g>
    <path d={cloudPath(100, 86)} strokeWidth={S} stroke="currentColor" fill="none" strokeLinejoin={LJ} />
    <polyline
      points="102,98 96,108 104,108 97,120"
      strokeWidth={S} stroke="currentColor" fill="none"
      strokeLinecap={LC} strokeLinejoin={LJ}
    />
    <line x1={84} y1={100} x2={82} y2={108} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
    <line x1={116} y1={100} x2={114} y2={108} strokeWidth={2.5} stroke="currentColor" strokeLinecap={LC} />
  </g>
);

const weatherIcons: Record<WeatherState, React.FC> = {
  'sunny': SunnyIcon,
  'mostly-sunny': MostlySunnyIcon,
  'partly-cloudy': PartlyCloudyIcon,
  'cloudy': CloudyIcon,
  'drizzle': DrizzleIcon,
  'rainy': RainyIcon,
  'stormy': StormyIcon,
};

const weatherStates: Record<WeatherState, string> = {
  'sunny': 'Sunny',
  'mostly-sunny': 'Mostly Sunny',
  'partly-cloudy': 'Partly Cloudy',
  'cloudy': 'Cloudy',
  'drizzle': 'Drizzle',
  'rainy': 'Rainy',
  'stormy': 'Stormy',
};

// ─── Main Component ──────────────────────────────────────

export const InnerWeatherIcon: React.FC<InnerWeatherIconProps> = ({
  a,
  b,
  c,
  className = '',
}) => {
  const weatherState = getWeatherState(a, b, c);
  const WeatherSvg = weatherIcons[weatherState];
  const weatherStateLabel = weatherStates[weatherState];
  return (
    <div className={`flex flex-col items-center ${className}`}>
      <svg viewBox="65 65 70 70" xmlns="http://www.w3.org/2000/svg" className="w-full flex-1 min-h-0" preserveAspectRatio="xMidYMid meet">
        <WeatherSvg />
      </svg>
      <span className="text-xs text-primary-5 font-light leading-tight">{weatherStateLabel}</span>
    </div>
  );
};
